import { useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { IGetMyDealerQuery } from '../../../../services/dealerService/__generated__/queries.generated';
import { GET_MY_DEALER } from '../../../../services/dealerService/queries';
import { ICar } from '../../../../../__generated__/graphql';
import BaseDialog from '../../../dialog';
import {
  useFindCarSaleQuery,
  useUpdateCarSaleMutation,
  IFindCarSaleQuery,
} from '../../../../services/carSaleServices/__generated__/queries.generated';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FIND_CAR_SALE } from '../../../../services/carSaleServices/queries';

const CarDetailsInfoSection = styled.div`
  ${tw`text-sm mb-4 px-4`}
`;

const VehicleDetailsTable = styled.table`
  ${tw`w-full border border-gray-600 mb-6 border-solid`}
`;

const VehicleTableCell = styled.td`
  ${tw`border border-gray-600 p-2 border-solid relative`}
`;

const CheckboxCell = styled.div`
  ${tw`flex items-center`}
`;

const Input = styled.input`
  ${tw`border border-gray-600 p-2 border-solid`}
`;

const EditButton = styled.button`
  ${tw`p-2 text-blue-600`}
  &:hover {
    ${tw`text-blue-800`}
  }
`;

const CarSelectContainer = styled.div`
  ${tw`grid grid-cols-1 gap-4 max-h-[400px] overflow-y-auto`}
`;

const CarSelectCard = styled.div<{ isSelected: boolean }>`
  ${tw`p-4 border border-gray-200 rounded-lg cursor-pointer`}
  ${({ isSelected }) => isSelected && tw` text-blue-600  bg-blue-100`}
  &:hover {
    ${tw`font-bold`}
  }
`;

const CarInfoSectionComponent = ({
  agreementData,
  agreementId,
}: {
  agreementData: IFindCarSaleQuery | undefined;
  agreementId: string;
}) => {
  const [selectedCar, setSelectedCar] = useState<Partial<ICar> | null>(null);
  const [odometer, setOdometer] = useState<string>('');
  const [isSelectingCar, setIsSelectingCar] = useState(false);

  const {
    data: dealerData,
    loading: dealerLoading,
    error: dealerError,
  } = useQuery<IGetMyDealerQuery>(GET_MY_DEALER);

  useEffect(() => {
    if (agreementData?.carSale?.id) {
      setSelectedCar(agreementData.carSale.car as Partial<ICar>);
      setOdometer(agreementData.carSale.odometerReading || '');
    }
  }, [agreementData]);

  const [UPDATE_CAR_SALE] = useUpdateCarSaleMutation({
    refetchQueries: [FIND_CAR_SALE],
    onCompleted: data => {
      toast.success('Buyer and seller info updated successfully');
      setIsSelectingCar(false);
    },
    onError: error => {
      toast.error(error.message);
    },
  });

  const handleCarSelect = (car: ICar) => {
    setSelectedCar(car);
  };

  const handleSaveOrUpdateBuyerAndSellerInfo = () => {
    if (agreementId) {
      UPDATE_CAR_SALE({
        variables: {
          input: {
            id: agreementId || '',
            carId: selectedCar?.id,
            odometerReading: odometer,
          },
        },
      });
    }
  };

  const renderVehicleDetailTable = () => {
    return (
      <VehicleDetailsTable onClick={() => setIsSelectingCar(true)}>
        <tbody>
          <tr>
            <VehicleTableCell>New/Used</VehicleTableCell>
            <VehicleTableCell>Year</VehicleTableCell>
            <VehicleTableCell>Make and Model</VehicleTableCell>
            <VehicleTableCell>Odometer</VehicleTableCell>
            <VehicleTableCell>Vehicle Identification Number</VehicleTableCell>
            <VehicleTableCell>
              Primary Use For Which Purchased
              <div>Personal, family, or household unless otherwise indicated below</div>
            </VehicleTableCell>
          </tr>
          <tr>
            <VehicleTableCell>Used</VehicleTableCell>
            <VehicleTableCell>{selectedCar?.year || '-'}</VehicleTableCell>
            <VehicleTableCell>{selectedCar?.make || '-'}</VehicleTableCell>
            <VehicleTableCell>{odometer || '-'}</VehicleTableCell>
            <VehicleTableCell>{selectedCar?.vin || '-'}</VehicleTableCell>
            <VehicleTableCell>
              <CheckboxCell>
                <input type="checkbox" /> business or commercial
              </CheckboxCell>
            </VehicleTableCell>
          </tr>
        </tbody>
      </VehicleDetailsTable>
    );
  };

  return (
    <>
      <CarDetailsInfoSection>
        You, the Buyer (and Co-Buyer, if any), may buy the vehicle below for cash or on credit. By
        signing this contract, you choose to buy the vehicle on credit under the agreements on the
        front and back of this contract. You agree to pay the Seller - Creditor (sometimes "we" or
        "us" in this contract) the Amount Financed and Finance Charge in U.S. funds according to the
        payment schedule below. We will figure your finance charge on a daily basis. The
        Truth-in-Lending Disclosures below are part of this contract.
      </CarDetailsInfoSection>

      {renderVehicleDetailTable()}

      <BaseDialog
        isOpen={isSelectingCar}
        onClose={() => setIsSelectingCar(false)}
        title={`Select a Car ${agreementId}`}
        description="Choose a car from your listed vehicles"
        confirmText="Save"
        cancelText="Cancel"
        onConfirm={handleSaveOrUpdateBuyerAndSellerInfo}
        renderChildren={() => (
          <CarSelectContainer>
            {dealerData?.myDealer?.cars?.map((car: Partial<ICar>) => (
              <CarSelectCard
                isSelected={selectedCar?.id === car.id}
                key={car.id}
                onClick={() => handleCarSelect(car as ICar)}
              >
                <div tw="font-bold">
                  {car.make} {car.model}
                </div>
                <div tw="text-sm text-gray-600">
                  Year: {car.year} | VIN: {car.vin}
                </div>
              </CarSelectCard>
            ))}
            <Input
              type="text"
              placeholder="Enter Odometer"
              value={odometer}
              pattern="[0-9]*"
              onChange={e => setOdometer(e.target.value)}
            />
          </CarSelectContainer>
        )}
      />
    </>
  );
};

export default CarInfoSectionComponent;
